import React, { useContext, useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ThemeContext } from 'styled-components';
import closeModalByName from '../../../actions/modal-fn/closeModalByName';
import ConfirmBar from '../../buttons/ConfirmBar';
import ErrorModal from '../ErrorModal';
import { ModalProps } from '../Modal';
import { StyledSection } from './ExitModal.styles';

export interface ExitModalProps<Data = {}> extends ModalProps<Data> {
  /** Text for the content. Can be null or undefined to not be displayed */
  modalMessage?: string;
  /** Custom text for the cancel button. Default "Cancel". */
  cancelButton?: string;
  /** Custom text for the confirm button. Default "Confirm". */
  confirmButton?: string;
  /** Display the cancel button as busy. */
  cancelBusy?: boolean;
  /** Display the confirm buttona as busy. */
  confirmBusy?: boolean;
  /** Disables the cancel button. */
  cancelDisabled?: boolean;
  /** Disables the confirm button. */
  confirmDisabled?: boolean;
  /** Callback for confirm button */
  onConfirm?: () => void;
  /** Callback for cancel button */
  onCancel?: () => void;
}

interface ExitModalInternalProps extends WithTranslation {}

interface ExitModalComposedProps<Data = {}> extends ExitModalProps<Data>, ExitModalInternalProps {}

const ExitModal = <Data = {}>({
  t,
  tReady,
  name,
  modalTitle,
  modalMessage,
  onCancel,
  onConfirm,
  cancelButton,
  confirmButton,
  cancelDisabled,
  cancelBusy,
  confirmBusy,
  confirmDisabled,
  ...props
}: ExitModalComposedProps<Data>) => {
  const dispatch = useDispatch();
  const theme = useContext(ThemeContext);
  const title = useMemo(() => modalTitle || t('title'), [modalTitle, t]);
  const isMessageNull = modalMessage === null;
  const message = useMemo(() => modalMessage || t('message'), [modalMessage, t]);

  return (
    <ErrorModal
      name={name}
      modalTitle={title}
      isClosable={theme?.Modal.closableErrorModal}
      {...props}
    >
      {({ name: modalName }) => (
        <>
          <ErrorModal.Content>
            {!isMessageNull && (
              <StyledSection>
                {message}
              </StyledSection>
            )}
          </ErrorModal.Content>
          <ErrorModal.Footer>
            <ConfirmBar
              confirmButtonFocus
              onCancel={onCancel || (() => dispatch(closeModalByName(modalName)))}
              onConfirm={onConfirm || (() => dispatch(closeModalByName(modalName)))}
              cancelButton={cancelButton}
              confirmButton={confirmButton}
              cancelDisabled={cancelDisabled}
              cancelBusy={cancelBusy}
              confirmBusy={confirmBusy}
              confirmDisabled={confirmDisabled}
              align="center"
            />
          </ErrorModal.Footer>
        </>
      )}
    </ErrorModal>
  );
};

ExitModal.displayName = 'ExitModal';

export default ExitModal;
